/**
 * Google Additional Consent (AC) Mode, ACv2
 * https://support.google.com/admanager/answer/9681920
 *
 * Carries consent for Google Ad Tech Providers that are not on the IAB GVL,
 * next to the TC string:
 *   <version>~<consented ATP IDs>~dv.<disclosed but not consented ATP IDs>
 *   2~1.35.41~dv.9.21   2~~dv.1.2.3   2~1.2.3~dv
 */

import bundledProviders from './ac-providers-bundled.json';

const AC_VERSION = 2;

// Google AC providers disclosed in this banner (from Google's commonly used providers list)
const acProviders = (bundledProviders.providers || [])
  .map(p => {
    const acId = (p.ids || []).find(x => x.framework === 'Google AC');
    return acId ? { id: acId.id, name: p.name, policyUrl: p.policy_url || '' } : null;
  })
  .filter(Boolean);

/**
 * Get the Google AC providers disclosed to the user
 */
export function getACProviders() {
  return acProviders;
}

/**
 * Get all disclosed Google AC provider IDs
 */
export function getAllACProviderIds() {
  return acProviders.map(p => p.id);
}

/**
 * Encode an ACv2 string
 * @param {Array<number>|Set<number>} consented - ATP IDs the user consented to
 * @param {Array<number>|Set<number>} disclosed - ATP IDs disclosed to the user
 * @returns {string}
 */
export function encodeACString(consented, disclosed) {
  const consentedSet = new Set(consented);
  // An ID in the consented block must not also appear in the disclosed block
  const disclosedIds = [...new Set(disclosed)].filter(id => !consentedSet.has(id));

  const consentedStr = sortedJoin(consentedSet);
  const disclosedStr = sortedJoin(disclosedIds);

  return `${AC_VERSION}~${consentedStr}~dv${disclosedStr ? '.' + disclosedStr : ''}`;
}

/**
 * Build the AC string for a consent choice
 * @param {boolean} consentToAll - Whether the user consented to every disclosed AC provider
 */
export function buildACString(consentToAll) {
  const ids = getAllACProviderIds();
  return encodeACString(consentToAll ? ids : [], ids);
}

function sortedJoin(ids) {
  return [...ids].sort((a, b) => a - b).join('.');
}
